import { ReactNode, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { fetchSecure } from "../utils/functions";
import useDelayedCallback from "./useDelayedCallback";
import { fetchSecureTotpNoMsg } from "../utils/MFA";
import { encryptAESCBC, decryptAESCBC } from "../utils/cryptoUtils";
import { getOrInitEncryptionKey } from "./CryptoHooks";

export interface FetchProps {
  url: RequestInfo | URL;
  options?: RequestInit;
}

export interface ResponseCallbacks {
  onPending?: () => ReactNode | void;
  onSuccess: (response: Response | any) => ReactNode | void;
  onError: (error: Error | any) => ReactNode | void;
  onFinally?: () => void;
}

export interface HookOptions {
  delay?: number;
  isSecure?: boolean;
  useTotp?: boolean;
  checkStatus?: boolean;
  notify?: boolean;
  ivEncrypt?: string;
  ivDecrypt?: string;
}

export class ErrorPDPFetch extends Error {
  errorJson: any;

  constructor(message: string, obj: any, ...params: any[]) {
    super(message, ...params);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ErrorPDPFetch);
    }

    this.name = "ErrorPDPFetch";
    this.errorJson = obj;
  }
}

const handlePDPFetchResponse = async (response: Response) => {
  const jsonResponse = await response.json();
  if ("status" in jsonResponse && !jsonResponse.status) {
    throw new ErrorPDPFetch(jsonResponse?.msg, jsonResponse, {
      cause: "custom",
    });
  }
  return jsonResponse;
};

const handleNotOk = async (response: Response) => {
  if (response.ok) {
    return response;
  }

  if (response.headers.get("Content-Type") === "application/json") {
    const jsonResponse = await response.json();
    throw new ErrorPDPFetch(
      jsonResponse?.msg ?? jsonResponse?.message ?? "Error en la peticion",
      jsonResponse,
      {
        cause: "not-ok",
      }
    );
  }

  throw new ErrorPDPFetch(response.statusText, await response.text(), {
    cause: "not-ok",
  });
};

const defaultOnPending: () => ReactNode = () => "Procesando peticion";
const defaultOnSuccess: (response: Response | any) => ReactNode = (_) =>
  "Peticion exitosa";
const defaultOnError: (error: Error | any) => ReactNode = (_) =>
  "Peticion fallida";

const useFetchDispatchDebounceECDH = (
  {
    onPending = defaultOnPending,
    onSuccess = defaultOnSuccess,
    onError = defaultOnError,
    onFinally,
  }: ResponseCallbacks = {
    onPending: defaultOnPending,
    onSuccess: defaultOnSuccess,
    onError: defaultOnError,
  },
  {
    delay = 500,
    isSecure = true,
    useTotp = false,
    checkStatus = true,
    notify = false,
    ivEncrypt,
    ivDecrypt,
  }: HookOptions = {
    delay: 500,
    isSecure: true,
    useTotp: false,
    checkStatus: true,
    notify: false,
  }
) => {
  const [loading, setLoading] = useState(false);
  const [abortController, setAbortController] = useState(new AbortController());

  const abort = useCallback(() => abortController.abort(), [abortController]);

  const peticion = useCallback(
    async (url: RequestInfo | URL, options?: RequestInit) => {
      const _abortController = new AbortController();
      setAbortController((old) => {
        old.abort();
        return _abortController;
      });
      try {
        setLoading(true);

        // Variables para la petición modificada
        let modifiedUrl = url;
        let modifiedOptions = options;

        // Solo encriptar si se proporcionó ivEncrypt
        if (ivEncrypt) {
          // Obtener la llave de encriptación ECDH
          const encryptionKey = await getOrInitEncryptionKey();

          const urlString = typeof url === "string" ? url : url.toString();
          const urlObj = new URL(urlString, window.location.origin);
          const method = options?.method?.toUpperCase() || "GET";

          if (method === "GET") {
            // Para GET: extraer query params, encriptarlos y ponerlos en la URL como ?data=...
            const queryParams: Record<string, string> = {};
            urlObj.searchParams.forEach((value, key) => {
              queryParams[key] = value;
            });

            if (Object.keys(queryParams).length > 0) {
              // Encriptar los query params
              const parseObj = JSON.stringify(queryParams);
              const encryptedData = await encryptAESCBC(
                encryptionKey,
                parseObj,
                ivEncrypt
              );

              // Crear nueva URL con solo el parámetro data encriptado
              modifiedUrl = `${urlObj.origin}${urlObj.pathname}?data=${encodeURIComponent(
                encryptedData
              )}`;
            }
          } else if (method === "POST" || method === "PUT") {
            // Para POST/PUT: encriptar el body
            if (options?.body) {
              let bodyData;
              if (typeof options.body === "string") {
                try {
                  bodyData = JSON.parse(options.body);
                } catch {
                  bodyData = options.body;
                }
              } else {
                bodyData = options.body;
              }

              // Encriptar el body
              const parseObj = JSON.stringify(bodyData);
              const encryptedData = await encryptAESCBC(
                encryptionKey,
                parseObj,
                ivEncrypt
              );

              modifiedOptions = {
                ...options,
                headers: {
                  ...options?.headers,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({ data: encryptedData }),
              };
            }
          }
        }

        // Realizar el fetch con la data encriptada
        let response;
        if (isSecure) {
          if (useTotp) {
            response = await fetchSecureTotpNoMsg(modifiedUrl, {
              credentials: "include",
              ...modifiedOptions,
              signal: _abortController.signal,
            });
          } else {
            response = await fetchSecure(modifiedUrl, {
              credentials: "include",
              ...modifiedOptions,
              signal: _abortController.signal,
            });
          }
        } else {
          response = await fetch(modifiedUrl, {
            credentials: "include",
            ...modifiedOptions,
            signal: _abortController.signal,
          });
        }

        if (!checkStatus) {
          return response;
        }

        let processedResponse = await handlePDPFetchResponse(
          await handleNotOk(response)
        );

        // Desencriptar la respuesta si tiene data encriptada y se proporcionó ivDecrypt
        if (ivDecrypt && processedResponse?.obj && processedResponse.obj?.data) {
          const encryptionKey = await getOrInitEncryptionKey();
          const decryptedData = await decryptAESCBC(
            encryptionKey,
            processedResponse.obj.data,
            ivDecrypt
          );
          processedResponse.obj = JSON.parse(decryptedData);
        }

        return processedResponse;
      } catch (error: any) {
        throw error;
      } finally {
        setLoading(false);
        onFinally?.();
      }
    },
    [isSecure, useTotp, checkStatus, onFinally, ivEncrypt, ivDecrypt]
  );

  const dispatchFetch = useCallback(
    async (url: RequestInfo | URL, options?: RequestInit) => {
      if (!notify) {
        onPending();
        return await peticion(url, options).then(onSuccess).catch(onError);
      }
      return await toast
        .promise(peticion(url, options), {
          pending: { type: "info", render: () => onPending() },
          error: {
            type: "warning",
            render: ({ data: error }) => onError(error),
          },
          success: { type: "info", render: ({ data }) => onSuccess(data) },
        })
        .catch(() => {});
    },
    [notify, onPending, onSuccess, onError, peticion]
  );

  const dispatcher = useDelayedCallback(dispatchFetch, delay);

  useEffect(() => {
    return () => {
      abortController.abort();
    };
  }, [abortController]);

  return [dispatcher, loading, abort] as const;
};

export default useFetchDispatchDebounceECDH;
